/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2024 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { isDay, ThemeSetting, useThemeSystemDetect } from "@advapacs/common";
import React, { createContext, useState, ReactNode, useEffect } from "react";

interface ThemeContextType {
  theme?: ThemeSetting;
  toggleTheme: (newTheme: ThemeSetting) => void;
}

export const ADVAPACS_STORAGE_THEME_NAME = "advapacs_theme";

export const ThemeContext = createContext<ThemeContextType | undefined>(
  undefined
);

const storageTheme =
  localStorage.getItem(ADVAPACS_STORAGE_THEME_NAME) || ThemeSetting.LIGHT;

const getCurrentTheme = (light?: boolean) => {
  let localBackgroundTheme =
    localStorage.getItem(ADVAPACS_STORAGE_THEME_NAME) || ThemeSetting.LIGHT;
  switch (storageTheme) {
    case ThemeSetting.SYSTEM:
      localBackgroundTheme = light ? ThemeSetting.LIGHT : ThemeSetting.DARK;
      break;
    case ThemeSetting.DARK:
      localBackgroundTheme = ThemeSetting.DARK;
      break;
    case ThemeSetting.LIGHT:
      localBackgroundTheme = ThemeSetting.LIGHT;
      break;

    default:
      break;
  }
  return localBackgroundTheme as ThemeSetting;
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const isDark = useThemeSystemDetect();

  const [theme, setTheme] = useState<ThemeSetting>(getCurrentTheme(!isDark));

  useEffect(() => {
    let localBackgroundTheme = ThemeSetting.LIGHT.toLowerCase(); // default is light for advapacs.
    switch (storageTheme) {
      case ThemeSetting.SYSTEM:
        localBackgroundTheme = isDark
          ? ThemeSetting.DARK.toLowerCase()
          : ThemeSetting.LIGHT.toLowerCase();
        break;
      case ThemeSetting.DARK:
        localBackgroundTheme = ThemeSetting.DARK.toLowerCase();
        break;
      case ThemeSetting.LIGHT:
        localBackgroundTheme = ThemeSetting.LIGHT.toLowerCase();
        break;

      default:
        break;
    }
    document.documentElement.setAttribute(
      "data-color-theme",
      localBackgroundTheme
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // default theme.

  const toggleTheme = (newTheme: ThemeSetting) => {
    setTheme(newTheme);
    document.documentElement.setAttribute(
      "data-color-theme",
      newTheme.toLowerCase()
    );
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
